import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { FileText } from 'lucide-react'
import { useState } from 'react'
import { documentsQuery, productsQuery, type DocumentKind, type StockDocument } from '@/api/goods'
import { CountSidesCell } from '@/app/goods/count-sides'
import { DocumentPanel } from '@/app/goods/document-panel'
import { GoodsTabs } from '@/app/goods/goods-shell'
import { MoreFooter } from '@/app/goods/more-footer'
import { ShelfFilter } from '@/app/goods/shelf'
import { usePaperNames } from '@/app/goods/use-paper-names'
import { useShelfFilter } from '@/app/goods/use-shelf'
import { useGoodsActions } from '@/app/goods/use-goods-actions'
import { NothingForRole } from '@/app/nothing-for-role'
import { StateFilter } from '@/ui/state-filter'
import { errorText } from '@/domain/errors'
import { goodsErrorText } from '@/domain/goods-errors'
import { paperTitle, whenText } from '@/domain/goods-text'
import { formatMoney } from '@/domain/pass-rules'
import { clubSearch, shelfFromKey, shelfKey } from '@/domain/shelves'
import { stocktakeMoney } from '@/domain/stocktake'
import { t } from '@/i18n'
import { cn } from '@/lib/cn'
import { withKey } from '@/lib/search'
import { DataList, type DataColumn } from '@/ui/data-list'
import { InlineBanner } from '@/ui/inline-banner'
import { EmptyState, Page, PageHeader } from '@/ui/page'
import { LoadingBlock } from '@/ui/spinner'

const FILTERS = ['all', 'receipt', 'write_off', 'sale', 'stocktake'] as const
type Filter = (typeof FILTERS)[number]

const isKind = (raw: unknown): raw is DocumentKind =>
  typeof raw === 'string' && raw !== 'all' && (FILTERS as readonly string[]).includes(raw)

export const Route = createFileRoute('/_app/c/$orgId/goods/documents')({
  validateSearch: (raw: Record<string, unknown>): { kind?: DocumentKind; club?: string } => ({
    ...(isKind(raw['kind']) ? { kind: raw['kind'] } : {}),
    ...clubSearch(raw['club']),
  }),
  component: Documents,
})

/// «Документи»: every receipt, write-off and sale, newest first — the papers
/// every on-hand figure is the sum of.
function Documents() {
  const { orgId } = Route.useParams()
  const { kind, club } = Route.useSearch()
  const navigate = Route.useNavigate()
  const shelf = shelfFromKey(club)
  const goods = useGoodsActions(orgId, { shelf })
  const { can } = goods
  const shelves = useShelfFilter(orgId, goods.access, can.papers)
  const names = usePaperNames(orgId, can.papers)
  const docs = useInfiniteQuery({
    ...documentsQuery(orgId, kind ?? null, shelf),
    enabled: can.see && can.papers,
    placeholderData: keepPreviousData,
  })
  const products = useQuery({ ...productsQuery(orgId), enabled: can.see })
  const [openId, setOpenId] = useState<string | null>(null)
  const x = t.goods.documents

  if (!can.see) return <NothingForRole />

  const byId = new Map((products.data ?? []).map((p) => [p.id, p]))
  const list = (docs.data?.pages ?? []).flatMap((page) => page.documents)
  const opened = list.find((doc) => doc.id === openId)
  const correctOpened = opened ? goods.correction(opened) : undefined

  // The paper first — what it is reads as the row's name; the date follows.
  const columns: readonly DataColumn<StockDocument>[] = [
    { key: 'doc', header: x.columns.doc, width: 'minmax(0,1.6fr)', mobile: 'title', cell: (doc) => paperTitle(doc) },
    { key: 'date', header: x.columns.date, width: 'minmax(140px,0.8fr)', mobile: 'subtitle', cell: (doc) => <span className="tabular">{whenText(doc.occurredAt)}</span> },
    ...(names.clubs > 0
      ? [
          {
            key: 'club',
            header: x.columns.club,
            width: 'minmax(0,0.9fr)',
            hideBelow: 'md',
            mobile: 'subtitle',
            cell: (doc: StockDocument) => names.club(doc.branchId),
          } satisfies DataColumn<StockDocument>,
        ]
      : []),
    {
      key: 'author',
      header: x.columns.author,
      width: 'minmax(0,1fr)',
      hideBelow: 'lg',
      mobile: 'hidden',
      cell: (doc) => names.author(doc.createdByUserId),
    },
    {
      key: 'items',
      header: x.columns.items,
      width: 'minmax(0,1.6fr)',
      hideBelow: 'xl',
      mobile: 'hidden',
      // A stocktake says how much it covered — its lines are only what
      // moved, and one that found everything in place has none.
      cell: (doc) => (
        <span className="truncate text-text-2">
          {doc.kind === 'stocktake'
            ? t.goods.count.listItems(doc.countedItems ?? 0, doc.lines.length)
            : doc.lines.map((l) => `${byId.get(l.productId)?.name ?? '…'} × ${l.qty}`).join(', ')}
        </span>
      ),
    },
    {
      key: 'total',
      header: x.columns.total,
      width: 'minmax(96px,0.7fr)',
      numeric: true,
      mobile: 'trailing',
      // A stocktake's two sides, the shortage first — never their net, which
      // made a count that lost three things read as a green «+42 ₴».
      cell: (doc) =>
        doc.kind === 'write_off' || doc.totalMinor === null ? (
          <span className="text-muted">—</span>
        ) : doc.kind === 'stocktake' ? (
          <CountSidesCell {...stocktakeMoney(doc.lines, (id) => byId.get(id)?.unit ?? '')} />
        ) : (
          formatMoney(doc.totalMinor)
        ),
    },
  ]

  return (
    <Page wide>
      <PageHeader title={t.goods.title} subtitle={t.goods.subtitle} actions={goods.actionsFor()} tabs={<GoodsTabs orgId={orgId} can={can} />} />
      {goods.news ? <InlineBanner kind="done" className="mb-6" message={goods.news} /> : null}

      {!can.papers ? (
        <InlineBanner kind="empty" message={x.noPapers} />
      ) : docs.isPending ? (
        <LoadingBlock label={t.app.loading} />
      ) : docs.isError ? (
        <InlineBanner kind="error" message={errorText(docs.error, goodsErrorText)} action={{ label: t.app.retry, onClick: () => void docs.refetch() }} />
      ) : (
        <div className={cn('flex flex-col gap-4 transition-opacity', docs.isPlaceholderData && 'opacity-60')}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <StateFilter
              label={x.filterLabel}
              value={(kind ?? 'all') as Filter}
              onChange={(next) => void navigate({ search: (prev) => withKey(prev, 'kind', next === 'all' ? undefined : next), replace: true })}
              options={FILTERS.map((f) => ({ value: f, label: x.filters[f] ?? f, count: undefined }))}
            />
            <ShelfFilter
              filter={shelves}
              value={shelf}
              onChange={(next) =>
                void navigate({ search: (prev) => withKey(prev, 'club', next.kind === 'all' ? undefined : shelfKey(next)), replace: true })
              }
            />
          </div>
          {list.length === 0 && !kind && shelf.kind === 'all' ? (
            <EmptyState icon={<FileText />} title={x.empty} message={x.emptyMessage} />
          ) : (
            <DataList
              label={t.goods.tabs.documents}
              columns={columns}
              rows={list}
              rowKey={(doc) => doc.id}
              rowTarget={(doc) => ({ onClick: () => setOpenId(doc.id) })}
              rowLabel={(doc) => `${paperTitle(doc)}, ${whenText(doc.occurredAt)}`}
              empty={x.emptyFilter}
              footer={
                <MoreFooter
                  shown={list.length}
                  pages={docs.data?.pages.length ?? 0}
                  hasMore={docs.hasNextPage}
                  loading={docs.isFetchingNextPage}
                  onMore={() => void docs.fetchNextPage()}
                />
              }
            />
          )}
        </div>
      )}

      {opened ? (
        <DocumentPanel
          doc={opened}
          products={byId}
          names={names}
          onClose={() => setOpenId(null)}
          onCorrect={
            correctOpened
              ? () => {
                  setOpenId(null)
                  correctOpened()
                }
              : undefined
          }
        />
      ) : null}
      {goods.panel}
    </Page>
  )
}
